import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext, Link, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Sparkles, CornerDownRight } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import { groupKeyForFecha } from './utils/helpers';
import { PriorityIcon, FechaText, AsignadosStack, AsignadoAvatar } from './utils/displays';
import { GRUPOS_FECHA } from './utils/constants';
import EmptyState from './EmptyState';
import { BUTTON } from './utils/uiTokens';
import PersonaSelector, { labelDeVista } from './PersonaSelector';

// Grupo de una fila por fecha. Las congeladas (esperan a una predecesora) van
// aparte: su plazo no corre, asi que no cuentan como vencidas.
const grupoFecha = (t) => (t.congelada ? 'espera' : groupKeyForFecha(t.fecha_vencimiento));

const esVencida = (t) => grupoFecha(t) === 'vencidas';

// ----- Fila -----
// Renderiza tanto tareas completas como filas de subtarea (item.es_subtarea).
// Las filas de subtarea vienen mezcladas en GET /api/tareas?asignado=me: muestran
// el texto de la subtarea, apuntan a su tarea padre (tarea_codigo) y van con un
// estilo levemente más tenue para distinguirlas. El click abre el slide-over de
// la TAREA PADRE (lo resuelve el handler del consumidor con item.tarea_id).

const TareaRow = ({ tarea, onClick }) => {
  const esSubtarea = tarea.es_subtarea === true;
  const proyectoColor = tarea.proyecto_color || '#06b6d4';
  const proyectoNombre = tarea.proyecto_nombre || tarea.proyecto_slug || '';
  const asignados = tarea.asignados || [];

  // Datos exclusivos de tareas completas (no aplican a subtareas).
  const codigo = tarea.codigo || `?-${tarea.numero}`;
  const etiquetas = esSubtarea ? [] : (tarea.etiquetas || []).slice(0, 3);
  const subtotal = esSubtarea ? 0 : (tarea.subtareas_total || 0);
  const subhechas = esSubtarea ? 0 : (tarea.subtareas_completadas || 0);

  return (
    <motion.div
      layout="position"
      initial={false}
      onClick={onClick}
      className="group grid grid-cols-[18px_72px_minmax(0,1fr)_auto_auto_auto] items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer hover:bg-slate-50"
    >
      {/* Prioridad (tarea) / marcador de subtarea */}
      <span className="flex items-center justify-center">
        {esSubtarea ? (
          <CornerDownRight size={12} className="text-slate-300" strokeWidth={2} />
        ) : (
          <PriorityIcon prioridad={tarea.prioridad} />
        )}
      </span>

      {/* ID: código de la tarea. En subtareas apunta a la tarea padre con "↳". */}
      {esSubtarea ? (
        <span
          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest text-slate-400 uppercase truncate"
          title={tarea.tarea_titulo ? `Subtarea de ${tarea.tarea_codigo} · ${tarea.tarea_titulo}` : `Subtarea de ${tarea.tarea_codigo}`}
        >
          <span className="text-slate-300">↳</span>
          {tarea.tarea_codigo}
        </span>
      ) : (
        <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
          {codigo}
        </span>
      )}

      {/* Título + (solo tareas) subtareas counter + etiquetas inline */}
      <span className="flex items-center gap-2 min-w-0">
        <span className={`truncate ${esSubtarea ? 'text-[13px] text-slate-600' : 'text-sm text-slate-800'}`}>
          {tarea.titulo}
        </span>
        {subtotal > 0 && (
          <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">
            {subhechas}/{subtotal}
          </span>
        )}
        {etiquetas.length > 0 && (
          <span className="flex items-center gap-1 flex-shrink-0">
            {etiquetas.map((e) => (
              <span
                key={e.id}
                title={e.nombre}
                className="inline-flex items-center gap-1 pl-1.5 pr-1.5 py-px rounded text-[9px] font-medium leading-none"
                style={{
                  backgroundColor: `${e.color || '#94a3b8'}1a`,
                  color: e.color || '#475569',
                }}
              >
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
                {e.nombre}
              </span>
            ))}
          </span>
        )}
      </span>

      {/* Proyecto */}
      <span className="inline-flex items-center gap-1.5 min-w-0 flex-shrink-0">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: proyectoColor }}
        />
        <span className="truncate text-[11px] text-slate-500 max-w-[140px]">
          {proyectoNombre}
        </span>
      </span>

      {/* Asignados + Fecha */}
      <span className="flex items-center justify-end gap-3 min-w-[72px]">
        <AsignadosStack asignados={asignados} color={proyectoColor} size={20} max={3} />
        <FechaText
          fecha={tarea.fecha_vencimiento}
          espera={tarea.congelada ? (tarea.bloqueadoras_pendientes || []).map((b) => b.codigo) : null}
        />
      </span>

      {/* Estado */}
      <span
        className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap"
        style={{
          backgroundColor: `${tarea.estado?.color || '#94a3b8'}1f`,
          color: tarea.estado?.color || '#475569',
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tarea.estado?.color || '#94a3b8' }} />
        {tarea.estado?.nombre || '—'}
      </span>
    </motion.div>
  );
};

// ----- Vista principal -----

const MisTareasView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchTerm = '' } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  // De quien son las tareas: 'me' | 'equipo' | 'todos' | <empleado_id>. Vive en
  // la URL (?ver=) para que la vista se pueda compartir y sobreviva al back.
  const ver = searchParams.get('ver') || 'me';
  const esPropia = ver === 'me';
  const [agruparPor, setAgruparPor] = useState(esPropia ? 'fecha' : 'persona');
  const [personas, setPersonas] = useState([]);
  const [tengoEquipo, setTengoEquipo] = useState(false);
  const queryVer = esPropia ? '' : `?ver=${ver}`;

  useEffect(() => { setAgruparPor(ver === 'me' || /^\d+$/.test(ver) ? 'fecha' : 'persona'); }, [ver]);

  useEffect(() => {
    axiosInstance.get('/api/tareas/personas')
      .then(({ data }) => {
        setPersonas(data?.personas || []);
        setTengoEquipo(!!data?.tengo_equipo);
      })
      .catch(() => {});
  }, []);

  const cambiarVer = useCallback((v) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'me') next.delete('ver'); else next.set('ver', v);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [tareas, setTareas] = useState([]);
  const [proyectosCache, setProyectosCache] = useState({}); // {slug -> proyecto detalle}
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  const [slideOverTareaId, setSlideOverTareaId] = useState(null);
  const [slideOverProyecto, setSlideOverProyecto] = useState(null);

  // Cargar mis tareas + mis proyectos (para color por proyecto)
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = /^\d+$/.test(ver) ? { asignado_id: ver } : { asignado: ver };
      const [tareasResp, proyectosResp] = await Promise.all([
        axiosInstance.get('/api/tareas', { params }),
        axiosInstance.get('/api/proyectos'),
      ]);
      // Enriquecer tareas con color del proyecto. Las filas de subtarea ya traen
      // proyecto_color del backend; lo usamos como respaldo si el proyecto no está
      // en /api/proyectos (p.ej. subtarea de un proyecto que no es mío directamente).
      const colorBySlug = {};
      (proyectosResp.data || []).forEach((p) => { colorBySlug[p.slug] = p.color; });
      const tareasEnriquecidas = (tareasResp.data || []).map((t) => ({
        ...t,
        proyecto_color: colorBySlug[t.proyecto_slug] || t.proyecto_color || '#06b6d4',
      }));
      setTareas(tareasEnriquecidas);
    } catch (err) {
      console.error('Error cargando mis tareas:', err);
    } finally {
      setLoading(false);
    }
  }, [ver]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Cargar proyecto a demanda (para el slide-over)
  const loadProyectoForSlideOver = useCallback(async (slug) => {
    if (proyectosCache[slug]) return proyectosCache[slug];
    try {
      const { data } = await axiosInstance.get(`/api/proyectos/${slug}`);
      setProyectosCache((prev) => ({ ...prev, [slug]: data }));
      return data;
    } catch (err) {
      console.error('Error cargando proyecto:', err);
      return null;
    }
  }, [proyectosCache]);

  // Filtrar + agrupar
  const grupos = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const filtradas = q
      ? tareas.filter(
          (t) =>
            (t.titulo || '').toLowerCase().includes(q) ||
            (t.codigo || '').toLowerCase().includes(q) ||
            (t.proyecto_slug || '').toLowerCase().includes(q) ||
            // Filas de subtarea: también matchean por su tarea padre.
            (t.tarea_codigo || '').toLowerCase().includes(q) ||
            (t.tarea_titulo || '').toLowerCase().includes(q)
        )
      : tareas;

    const porFecha = (a, b) => {
      if (!a.fecha_vencimiento && !b.fecha_vencimiento) return 0;
      if (!a.fecha_vencimiento) return 1;
      if (!b.fecha_vencimiento) return -1;
      return a.fecha_vencimiento.localeCompare(b.fecha_vencimiento);
    };

    // Por persona: una seccion por asignado, con su carga. Una tarea con dos
    // asignados aparece en ambas. En "Mi equipo" solo se muestran las personas
    // del equipo (no los co-asignados de fuera).
    if (agruparPor === 'persona') {
      const equipoIds = new Set(personas.filter((p) => p.es_equipo).map((p) => p.id));
      const map = new Map();
      filtradas.forEach((t) => {
        (t.asignados || []).forEach((a) => {
          if (ver === 'equipo' && !equipoIds.has(a.id)) return;
          if (/^\d+$/.test(ver) && String(a.id) !== ver) return;
          if (!map.has(a.id)) map.set(a.id, { nombre: a.nombre, items: [] });
          map.get(a.id).items.push(t);
        });
      });
      return [...map.entries()]
        .map(([id, g]) => {
          const vencidas = g.items.filter(esVencida).length;
          const ordenados = [...g.items].sort((a, b) => {
            // vencidas primero, luego por fecha; en espera al final
            const ra = esVencida(a) ? 0 : a.congelada ? 2 : 1;
            const rb = esVencida(b) ? 0 : b.congelada ? 2 : 1;
            return ra - rb || porFecha(a, b);
          });
          return {
            key: `persona-${id}`,
            label: g.nombre,
            persona: true,
            vencidas,
            color: '#64748b',
            items: ordenados,
          };
        })
        .sort((a, b) => b.vencidas - a.vencidas || b.items.length - a.items.length);
    }

    const map = new Map();
    filtradas.forEach((t) => {
      const k = grupoFecha(t);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    });
    map.forEach((items) => items.sort(porFecha));

    return GRUPOS_FECHA.filter((g) => map.has(g.key)).map((g) => ({
      ...g,
      items: map.get(g.key),
    }));
  }, [tareas, searchTerm, agruparPor, personas, ver]);

  // Filas unicas (en vista por persona una tarea puede repetirse).
  const total = useMemo(
    () => new Set(grupos.flatMap((g) => g.items.map((t) => t.id))).size,
    [grupos]
  );
  const totalVencidas = useMemo(
    () => new Set(grupos.flatMap((g) => g.items.filter(esVencida).map((t) => t.id))).size,
    [grupos]
  );

  // ----- Click en fila -----

  // Click en una tarea: navegar al URL canónico del proyecto. Esto saca al
  // usuario de "Mis tareas" pero garantiza URLs uniformes y compartibles
  // (mismo formato que copyLink). El back del navegador regresa a Mis tareas.
  //
  // Click en una fila de subtarea: abrir el slide-over de la TAREA PADRE. No
  // tenemos el `numero` de la tarea padre (solo su id en tarea_id), así que
  // usamos la ruta in-place /mis-tareas/tareas/:id, que resuelve la tarea por id
  // (en mi lista o vía fetch directo). NUNCA usar tarea.id (es 'sub-<n>').
  const handleRowClick = useCallback((tarea) => {
    if (tarea.es_subtarea) {
      navigate(`/proyectos/mis-tareas/tareas/${tarea.tarea_id}${queryVer}`);
      return;
    }
    navigate(`/proyectos/${tarea.proyecto_slug}/tareas/${tarea.numero}`);
  }, [navigate, queryVer]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    setSlideOverProyecto(null);
    navigate(`/proyectos/mis-tareas${queryVer}`, { replace: false });
  }, [navigate, queryVer]);

  // Sincronizar slide-over con URL /proyectos/mis-tareas/tareas/:id. Depende de
  // location.pathname para reaccionar a back/forward del navegador. Si la tarea
  // no esta en mi lista (link compartido, ya no me la asignaron, etc), hacemos
  // fetch directo para abrirla igual.
  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/mis-tareas\/tareas\/(\d+)/);
    if (!match) {
      setSlideOverTareaId(null);
      setSlideOverProyecto(null);
      return;
    }
    const id = parseInt(match[1], 10);
    if (tareas.length === 0 && loading) return; // todavia cargando
    // Buscar la tarea como fila completa o, si se abrió desde una subtarea,
    // como la tarea padre de alguna fila de subtarea (que conoce su proyecto_slug).
    const tarea =
      tareas.find((t) => !t.es_subtarea && t.id === id) ||
      tareas.find((t) => t.es_subtarea && t.tarea_id === id);
    if (tarea) {
      setSlideOverTareaId(id);
      loadProyectoForSlideOver(tarea.proyecto_slug).then((p) => {
        if (p) setSlideOverProyecto(p);
      });
      return;
    }
    // No esta en mi lista: fetch directo.
    axiosInstance.get(`/api/tareas/${id}`)
      .then(({ data }) => {
        if (!data) return;
        setSlideOverTareaId(id);
        loadProyectoForSlideOver(data.proyecto_slug).then((p) => {
          if (p) setSlideOverProyecto(p);
        });
      })
      .catch(() => {
        // 404/403: no podemos abrirla, redirigimos a la lista limpia.
        navigate(`/proyectos/mis-tareas${queryVer}`, { replace: true });
      });
  }, [tareas, loading, location.pathname, loadProyectoForSlideOver, navigate, queryVer]);

  // ----- Navegacion prev/next -----

  // Id real de tarea de una fila: en subtareas es el de la tarea padre (tarea_id),
  // nunca el id de la fila ('sub-<n>'). Las flechas siempre apuntan a una tarea.
  const tareaIdDeFila = useCallback(
    (item) => (item.es_subtarea ? item.tarea_id : item.id),
    []
  );

  const handleNavigateSlideOver = useCallback(async (dir) => {
    if (!slideOverTareaId) return;
    const flat = grupos.flatMap((g) => g.items).filter(
      (t, i, arr) => arr.findIndex((x) => x.id === t.id) === i
    );
    const idx = flat.findIndex((t) => tareaIdDeFila(t) === slideOverTareaId);
    if (idx === -1) return;
    const nextIdx = dir === 'next' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= flat.length) return;
    const next = flat[nextIdx];
    const nextTareaId = tareaIdDeFila(next);
    setSlideOverTareaId(nextTareaId);
    const proyecto = await loadProyectoForSlideOver(next.proyecto_slug);
    if (proyecto) setSlideOverProyecto(proyecto);
    navigate(`/proyectos/mis-tareas/tareas/${nextTareaId}${queryVer}`, { replace: false });
  }, [slideOverTareaId, grupos, navigate, loadProyectoForSlideOver, tareaIdDeFila, queryVer]);

  // ----- Render -----

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 pt-8 pb-12">
        <div className="mb-8 skeleton">
          <div className="h-6 w-32 bg-slate-100 rounded mb-2" />
          <div className="h-4 w-72 bg-slate-100/70 rounded" />
        </div>
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white border border-slate-200/70 rounded-xl overflow-hidden skeleton">
              <div className="px-4 py-2.5 bg-slate-50/40 border-b border-slate-100 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                <div className="h-3 w-20 bg-slate-100 rounded" />
                <div className="h-3 w-6 bg-slate-100/70 rounded" />
              </div>
              <div className="divide-y divide-slate-100">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-slate-100" />
                    <div className="h-3 w-14 bg-slate-100/70 rounded" />
                    <div className="h-3 flex-1 max-w-[280px] bg-slate-100 rounded" />
                    <div className="h-3 w-20 bg-slate-100/70 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-8 pt-8 pb-12">
      {/* Header de seccion */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4 mb-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 truncate">
            {esPropia ? 'Mis tareas' : ver === 'equipo' ? 'Tareas de mi equipo' : ver === 'todos' ? 'Tareas de todas las personas' : `Tareas de ${labelDeVista(ver, personas)}`}
          </h1>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-slate-400 tabular-nums">
              {total} {total === 1 ? 'tarea' : 'tareas'}
              {totalVencidas > 0 && <span className="text-rose-600"> · {totalVencidas} vencidas</span>}
            </span>
            {!esPropia && (
              <div className="inline-flex items-center h-8 p-0.5 rounded-md bg-slate-100 text-xs" role="group" aria-label="Agrupar por">
                {[['persona', 'Por persona'], ['fecha', 'Por fecha']].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setAgruparPor(k)}
                    aria-pressed={agruparPor === k}
                    className={`h-7 px-2.5 rounded font-medium transition-colors ${
                      agruparPor === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <PersonaSelector
              valor={ver}
              personas={personas}
              tengoEquipo={tengoEquipo}
              onChange={cambiarVer}
            />
          </div>
        </div>
        <p className="text-sm text-slate-500">
          {esPropia
            ? 'Todas las tareas asignadas a ti, en todos tus proyectos.'
            : ver === 'equipo'
              ? 'Tareas de las personas que te reportan, en los proyectos donde eres miembro.'
              : ver === 'todos'
                ? 'Tareas de todas las personas en los proyectos donde eres miembro.'
                : 'Sus tareas en los proyectos donde eres miembro.'}
        </p>
      </div>

      {total === 0 ? (
        searchTerm ? (
          <div className="flex items-center gap-3 py-10 px-5 bg-white border border-slate-200/70 rounded-xl">
            <Sparkles size={16} className="text-slate-300 flex-shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700">
                Sin resultados para <span className="font-medium text-slate-900">"{searchTerm}"</span>
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Prueba con otra búsqueda o limpia el filtro.</p>
            </div>
          </div>
        ) : (
          <div className="py-24">
            <EmptyState
              icon={Sparkles}
              title={esPropia ? 'No tienes tareas asignadas' : 'No hay tareas abiertas'}
              description={
                esPropia
                  ? 'Cuando alguien te asigne una tarea, va a aparecer acá.'
                  : ver === 'equipo' && !tengoEquipo
                    ? 'No encontramos personas que te reporten en el organigrama.'
                    : 'No hay tareas asignadas para esta selección en tus proyectos.'
              }
              variant="centered"
              action={
                <Link to="/proyectos" className={`${BUTTON.secondary} ${BUTTON.size.md}`}>
                  Ver proyectos
                </Link>
              }
            />
          </div>
        )
      ) : (
        <div className="space-y-4">
          {grupos.map((g) => {
            const isCollapsed = collapsed[g.key];
            const Icon = g.icon;
            return (
              <section key={g.key} className="bg-white border border-slate-200/70 rounded-xl overflow-hidden">
                {/* Header del panel */}
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                  className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50/40 border-b border-slate-100 group"
                >
                  <motion.span
                    animate={{ rotate: isCollapsed ? 0 : 90 }}
                    transition={{ duration: 0.15 }}
                    className="text-slate-300 group-hover:text-slate-500"
                  >
                    <ChevronRight size={11} strokeWidth={2.5} />
                  </motion.span>
                  {g.persona ? (
                    <>
                      <AsignadoAvatar nombre={g.label} size={20} />
                      <span className="text-[13px] font-semibold text-slate-800 tracking-tight">{g.label}</span>
                      <span className="text-[11px] text-slate-400 tabular-nums">
                        {g.items.length} {g.items.length === 1 ? 'tarea' : 'tareas'}
                      </span>
                      {g.vencidas > 0 && (
                        <span className="text-[11px] font-medium text-rose-600 tabular-nums">
                          {g.vencidas} {g.vencidas === 1 ? 'vencida' : 'vencidas'}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: g.color }} />
                      <Icon size={11} style={{ color: g.color }} strokeWidth={2} />
                      <span
                        className="text-[11px] font-bold tracking-[0.1em] uppercase"
                        style={{ color: g.key === 'vencidas' ? '#ef4444' : '#475569' }}
                      >
                        {g.label}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 tabular-nums">
                        {g.items.length}
                      </span>
                    </>
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="divide-y divide-slate-100"
                    >
                      {g.items.map((tarea) => (
                        <TareaRow
                          key={tarea.id}
                          tarea={tarea}
                          onClick={() => handleRowClick(tarea)}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })}
        </div>
      )}

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={slideOverProyecto}
        onClose={handleCloseSlideOver}
        onUpdate={(tareaFresca) => {
          // Solo refrescamos filas de tarea completa (las de subtarea tienen su
          // propio shape e id 'sub-<n>'). Preservar proyecto_color (campo derivado
          // que no viene del endpoint).
          setTareas((prev) => prev.map((t) =>
            !t.es_subtarea && t.id === tareaFresca.id
              ? { ...t, ...tareaFresca, proyecto_color: t.proyecto_color }
              : t
          ));
        }}
        onArchive={() => {
          // Sacarla del state local en vez de refetchAll completo (que pegaba
          // a /proyectos y /tareas otra vez por una sola accion). También se
          // retiran las filas de subtarea cuya tarea padre fue archivada.
          setTareas((prev) => prev.filter((t) =>
            t.es_subtarea
              ? t.tarea_id !== slideOverTareaId
              : t.id !== slideOverTareaId
          ));
          handleCloseSlideOver();
        }}
        onDuplicate={(nueva) => { fetchAll(); setSlideOverTareaId(nueva.id); }}
        onNavigate={handleNavigateSlideOver}
      />
    </div>
  );
};

export default MisTareasView;
